/**
 * Meridian renderer — faithful port of meridian-generator-final.html.
 *
 * Particle-based sand/strata simulation producing geological landscape layers.
 * Uses Perlin-noise-driven wind, per-grain color variation, edge lines,
 * extended-canvas meniscus fix, and palette-derived gradient background.
 *
 * Exports:
 *   renderMeridian(ctx, W, H, palette, params, rand)         — single-shot
 *   renderMeridianAnimated(ctx, W, H, palette, params, rand)  — layer-by-layer animated (returns Promise)
 */

// ═══════════════════════════════════════════════════════════
//  Perlin Noise — ported from meridian-generator-final.html
// ═══════════════════════════════════════════════════════════

class Noise {
  constructor(s) {
    this.p = new Uint8Array(512);
    const q = new Uint8Array(256);
    for (let i = 0; i < 256; i++) q[i] = i;
    for (let i = 255; i > 0; i--) {
      s = (s * 16807 + 11) % 2147483647;
      const j = s % (i + 1);
      [q[i], q[j]] = [q[j], q[i]];
    }
    for (let i = 0; i < 512; i++) this.p[i] = q[i & 255];
  }
  fade(t) { return t * t * t * (t * (t * 6 - 15) + 10); }
  lerp(a, b, t) { return a + t * (b - a); }
  grad(h, x, y) {
    const s = h & 3;
    return s === 0 ? x + y : s === 1 ? -x + y : s === 2 ? x - y : -x - y;
  }
  n2d(x, y) {
    const X = Math.floor(x) & 255, Y = Math.floor(y) & 255;
    const xf = x - Math.floor(x), yf = y - Math.floor(y);
    const u = this.fade(xf), v = this.fade(yf);
    const p = this.p;
    const a = p[X] + Y, b = p[X + 1] + Y;
    return this.lerp(
      this.lerp(this.grad(p[a], xf, yf), this.grad(p[b], xf - 1, yf), u),
      this.lerp(this.grad(p[a + 1], xf, yf - 1), this.grad(p[b + 1], xf - 1, yf - 1), u),
      v
    );
  }
  fbm(x, y, o = 3) {
    let v = 0, a = 1, f = 1, m = 0;
    for (let i = 0; i < o; i++) {
      v += this.n2d(x * f, y * f) * a;
      m += a; a *= 0.5; f *= 2;
    }
    return v / m;
  }
}

// ═══════════════════════════════════════════════════════════
//  SIMULATION CORE — builds plans + simulates grains
// ═══════════════════════════════════════════════════════════

function buildSimulation(W, H, pal, P, rand) {
  const {
    layers = 7,
    windStrength = 0.3,
    reposeAngle = 2,
    nozzleVar = 3,
  } = P;

  const bgRgb = parseColor(pal.bg);
  const rgbColors = pal.colors.map(c => parseColor(c));
  const numLayers = layers;
  const windStr = windStrength / 10;
  const angleOfRepose = reposeAngle;
  const nzVar = nozzleVar;

  const seedBase = Math.floor(rand() * 2147483647);
  const nozzleNoise = new Noise(seedBase + 1111);
  const windNoise = new Noise(seedBase + 2222);
  const windNoise2 = new Noise(seedBase + 3333);
  const grainRand = createLocalRng(seedBase + 7777);
  const planRand = createLocalRng(seedBase + 5555);

  const EDGE_PAD = Math.max(40, Math.round(W * 0.04));
  const simW = W + EDGE_PAD * 2;
  const simH = H;

  const surface = new Int16Array(simW);
  for (let x = 0; x < simW; x++) surface[x] = simH - 1;

  const imgBuf = new Uint8ClampedArray(simW * simH * 4);

  // Flat bg fill — particles cover everything else
  for (let i = 0; i < simW * simH; i++) {
    const off = i * 4;
    imgBuf[off] = bgRgb[0]; imgBuf[off + 1] = bgRgb[1]; imgBuf[off + 2] = bgRgb[2]; imgBuf[off + 3] = 255;
  }

  // Layer plans
  const totalGrains = Math.round(simW * simH * 2.2);
  const basePerLayer = totalGrains / numLayers;

  const layerPlans = [];
  for (let i = 0; i < numLayers; i++) {
    const colorIdx = i % rgbColors.length;
    const thickness = 0.5 + planRand() * 1.0;
    const grainCount = Math.round(basePerLayer * thickness);
    const sweepDir = (i % 2 === 0) ? 1 : -1;
    const nozzleSpeedScale = 0.5 + planRand() * 1.5;
    const windPhase = planRand() * 100;
    layerPlans.push({ colorIdx, grainCount, sweepDir, nozzleSpeedScale, windPhase });
  }

  const rawTotal = layerPlans.reduce((s, l) => s + l.grainCount, 0);
  const scale = totalGrains / rawTotal;
  for (const p of layerPlans) p.grainCount = Math.round(p.grainCount * scale);

  return {
    layerPlans, numLayers, rgbColors, bgRgb, simW, simH, EDGE_PAD,
    surface, imgBuf, nozzleNoise, windNoise, windNoise2,
    grainRand, windStr, angleOfRepose, nzVar, seedBase,
  };
}

function simulateLayer(sim, layerIndex) {
  const {
    layerPlans, simW, simH, rgbColors, surface, imgBuf,
    nozzleNoise, windNoise, windNoise2, grainRand, windStr,
    angleOfRepose, nzVar,
  } = sim;

  const plan = layerPlans[layerIndex];
  const baseRgb = rgbColors[plan.colorIdx];
  const gc = plan.grainCount;

  for (let g = 0; g < gc; g++) {
    const progress = g / gc;
    let nozzleX;
    if (plan.sweepDir > 0) nozzleX = progress * simW;
    else nozzleX = (1 - progress) * simW;

    const noiseWarp = nozzleNoise.fbm(
      progress * nzVar * plan.nozzleSpeedScale + layerIndex * 3.7,
      layerIndex * 2.1, 3
    );
    nozzleX += noiseWarp * simW * 0.3 * nzVar / 5;
    nozzleX += (grainRand() - 0.5) * 30;

    let dropX = Math.round(nozzleX);
    if (dropX < 0) dropX = 0;
    if (dropX >= simW) dropX = simW - 1;

    const fallHeight = surface[dropX] >= 0 ? surface[dropX] : 0;
    const windForce = windNoise.fbm(dropX / simW * 2 + plan.windPhase, progress * 3 + layerIndex * 1.7, 3);
    const windGust = windNoise2.fbm(dropX / simW * 4 + plan.windPhase * 0.7, progress * 5 + layerIndex * 0.9, 2) * 0.5;
    const maxDisplace = windStr * 350;
    const displace = Math.round((windForce + windGust) * maxDisplace * Math.sqrt(fallHeight / simH));

    let landX = dropX + displace;
    if (landX < 0) landX = 0;
    if (landX >= simW) landX = simW - 1;

    let x = landX;
    let slides = 0;
    while (slides < 250) {
      const y = surface[x];
      if (y < 0) {
        let found = false;
        for (let d = 1; d < 80; d++) {
          if (x - d >= 0 && surface[x - d] >= 0) { x = x - d; found = true; break; }
          if (x + d < simW && surface[x + d] >= 0) { x = x + d; found = true; break; }
        }
        if (!found) break;
        continue;
      }
      const leftOk = x > 0, rightOk = x < simW - 1;
      const leftDrop = leftOk ? (surface[x - 1] - y) : -999;
      const rightDrop = rightOk ? (surface[x + 1] - y) : -999;

      if (leftDrop > angleOfRepose && rightDrop > angleOfRepose) {
        x += (leftDrop > rightDrop) ? -1 : (rightDrop > leftDrop) ? 1 : ((grainRand() > 0.5) ? -1 : 1);
        slides++;
      } else if (leftDrop > angleOfRepose) { x--; slides++; }
      else if (rightDrop > angleOfRepose) { x++; slides++; }
      else {
        const bri = (grainRand() - 0.5) * 44;
        const hue = (grainRand() - 0.5) * 16;
        const off = (y * simW + x) * 4;
        imgBuf[off] = Math.max(0, Math.min(255, baseRgb[0] + bri + hue));
        imgBuf[off + 1] = Math.max(0, Math.min(255, baseRgb[1] + bri));
        imgBuf[off + 2] = Math.max(0, Math.min(255, baseRgb[2] + bri - hue));
        imgBuf[off + 3] = 255;
        surface[x] = y - 1;
        break;
      }
    }
  }
}

function cropToOutput(ctx, sim, W, H) {
  const { imgBuf, simW, EDGE_PAD } = sim;
  const outputData = ctx.createImageData(W, H);
  const out = outputData.data;
  for (let py = 0; py < H; py++) {
    const srcRow = py * simW + EDGE_PAD;
    const dstRow = py * W;
    for (let px = 0; px < W; px++) {
      const si = (srcRow + px) * 4;
      const di = (dstRow + px) * 4;
      out[di] = imgBuf[si]; out[di + 1] = imgBuf[si + 1];
      out[di + 2] = imgBuf[si + 2]; out[di + 3] = 255;
    }
  }
  ctx.putImageData(outputData, 0, 0);
}

// ═══════════════════════════════════════════════════════════
//  PUBLIC API — single-shot render
// ═══════════════════════════════════════════════════════════

export function renderMeridian(ctx, W, H, pal, P, rand) {
  const sim = buildSimulation(W, H, pal, P, rand);

  for (let li = 0; li < sim.numLayers; li++) {
    simulateLayer(sim, li);
  }

  cropToOutput(ctx, sim, W, H);
}

// ═══════════════════════════════════════════════════════════
//  PUBLIC API — animated layer-by-layer render (returns Promise)
// ═══════════════════════════════════════════════════════════

export function renderMeridianAnimated(ctx, W, H, pal, P, rand) {
  const sim = buildSimulation(W, H, pal, P, rand);
  let layer = 0;

  // Draw initial background
  cropToOutput(ctx, sim, W, H);

  return new Promise((resolve) => {
    function step() {
      if (layer >= sim.numLayers) {
        resolve();
        return;
      }
      simulateLayer(sim, layer);
      cropToOutput(ctx, sim, W, H);
      layer++;
      requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  });
}

// ═══════════════════════════════════════════════════════════
//  HELPERS
// ═══════════════════════════════════════════════════════════

function parseColor(c) {
  if (typeof c === 'string' && c.startsWith('rgb')) {
    const m = c.match(/\d+/g);
    if (m) return [parseInt(m[0]), parseInt(m[1]), parseInt(m[2])];
  }
  if (typeof c === 'string' && c.startsWith('#') && c.length >= 7) {
    return [parseInt(c.slice(1, 3), 16), parseInt(c.slice(3, 5), 16), parseInt(c.slice(5, 7), 16)];
  }
  return [128, 128, 128];
}

function createLocalRng(s) {
  let k = s | 0;
  return () => {
    k = (k + 0x6D2B79F5) | 0;
    let t = Math.imul(k ^ (k >>> 15), 1 | k);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
